import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Star, Upload, X, CloudOff, Save, Edit2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOfflineDrafts } from "@/hooks/useOfflineDrafts";
import { OnlineStatusBadge } from "@/components/SyncStatusIndicator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DynamicCustomFields } from "@/components/DynamicCustomFields";
import PhotoUpload, { type PhotoFile } from "@/components/PhotoUpload";
import PhotoGallery from "@/components/PhotoGallery";
import { FieldPhotoCapture, type FieldPhoto } from "@/components/FieldPhotoCapture";
import { DuplicateLogModal } from "@/components/DuplicateLogModal";
import { FieldLabel } from "@/components/FieldLabel";
import { CorrectionsHistory } from "@/components/CorrectionsHistory";
import { CorrectFieldModal } from "@/components/CorrectFieldModal";
import { DAILY_LOG_HELP_TEXT, CHEMICAL_LOG_HELP_TEXT } from "../../../shared/fieldHelpText";
import { TRPCClientError } from "@trpc/client";
import { enqueueDailyLog, getOutboxCounts, type OutboxCounts } from "@/lib/offlineOutbox";
import { syncPendingDailyLogs } from "@/lib/offlineSync";
import { buildChemicalUpsertPayload } from "@/lib/chemicals";
import { WifiOff, RefreshCw } from "lucide-react";


export default function DailyLogEntry() {
  const { user } = useAuth();
  const params = useParams();
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const logId = params.id ? parseInt(params.id) : null;
  const isEdit = logId !== null;

  // Defensive route param guard
  if (isEdit && (!params.id || isNaN(parseInt(params.id)))) {
    toast.error("Invalid log ID");
    navigate("/logs");
    return null;
  }

  // Parse URL parameters for QR code deep-linking
  const urlParams = new URLSearchParams(searchString);
  const qrWellId = urlParams.get('wellId');
  const qrFocusField = urlParams.get('focusField');

  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [logTime, setLogTime] = useState(new Date().toTimeString().slice(0, 5));
  const [operatorInitial, setOperatorInitial] = useState(user?.operatorInitial || "");
  const [meterReadStart, setMeterReadStart] = useState("");
  const [meterReadEnd, setMeterReadEnd] = useState("");
  const [expectedMeterStart, setExpectedMeterStart] = useState<string | null>(null);
  const [showMeterWarning, setShowMeterWarning] = useState(false);
  const [meterDiscrepancy, setMeterDiscrepancy] = useState<number>(0);
  const [gpm, setGpm] = useState("");
  const [hours, setHours] = useState("");
  const [remarks, setRemarks] = useState("");
  
  // Chemical fields
  const [measuredInchesCl2, setMeasuredInchesCl2] = useState("");
  const [inchesUsedCl2, setInchesUsedCl2] = useState("");
  const [inchesNaOClWithDelivery, setInchesNaOClWithDelivery] = useState("");
  const [lbsCl2, setLbsCl2] = useState("");
  const [freeResidualPpm, setFreeResidualPpm] = useState("");
  const [psi, setPsi] = useState("");
  const [staticValue, setStaticValue] = useState("");
  const [pumpingValue, setPumpingValue] = useState("");
  const [topOffCl2, setTopOffCl2] = useState("");
  const [residual, setResidual] = useState("");
  const [chemicalNotes, setChemicalNotes] = useState("");
  
  // Duplicate handling
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [existingDuplicateLog, setExistingDuplicateLog] = useState<any>(null);
  
  // Photo attachments
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [fieldPhotos, setFieldPhotos] = useState<FieldPhoto[]>([]);

  // Field photo handlers
  const handleFieldPhotoCapture = (photo: FieldPhoto) => {
    setFieldPhotos(prev => [...prev, photo]);
  };

  const handleFieldPhotoRemove = (photo: FieldPhoto) => {
    setFieldPhotos(prev => prev.filter(p => p !== photo));
    // Revoke object URL to free memory
    URL.revokeObjectURL(photo.preview);
  };
  
  // Custom fields
  const [dailyLogExtraJson, setDailyLogExtraJson] = useState<Record<string, any>>({});
  const [chemicalLogExtraJson, setChemicalLogExtraJson] = useState<Record<string, any>>({});
  const [showValidation, setShowValidation] = useState(false);

  // Edit reason (required when updating existing logs)
  const [editReason, setEditReason] = useState("");

  // Correction modal state
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [correctionField, setCorrectionField] = useState<{
    fieldName: string;
    fieldLabel: string;
    currentValue: string | null;
  } | null>(null);

  // Offline outbox status
  const [outboxCounts, setOutboxCounts] = useState<OutboxCounts>({ pending: 0, failed: 0 });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const { data: dailyLogCustomFields } = trpc.customFields.listByTarget.useQuery({ targetTable: "dailyLogs" });
  const { data: chemicalLogCustomFields } = trpc.customFields.listByTarget.useQuery({ targetTable: "chemicalLogs" });

  const { data: wells } = trpc.wells.list.useQuery({ activeOnly: true });
  const { data: favorites } = trpc.favorites.list.useQuery();
  const { data: recentWells } = trpc.recentWells.list.useQuery();
  const { data: existingLog } = trpc.dailyLogs.getById.useQuery(
    { id: logId! },
    { enabled: isEdit }
  );

  // Get previous meter reading for continuity checking
  const { data: previousMeterData } = trpc.dailyLogs.getPreviousMeterReading.useQuery(
    {
      wellId: selectedWellId!,
      beforeDate: new Date(logDate),
    },
    {
      enabled: !isEdit && selectedWellId !== null && logDate !== '',
    }
  );

  const createLogMutation = trpc.dailyLogs.create.useMutation();
  const updateLogMutation = trpc.dailyLogs.update.useMutation();
  const upsertChemicalMutation = trpc.chemicalLogs.upsert.useMutation();
  const uploadPhotoMutation = trpc.attachments.upload.useMutation();
  const addFavoriteMutation = trpc.favorites.add.useMutation();
  const removeFavoriteMutation = trpc.favorites.remove.useMutation();

  const utils = trpc.useUtils();

  // Auto-fill meter start from previous log
  useEffect(() => {
    if (!isEdit && previousMeterData?.expectedMeterStart && !meterReadStart) {
      setMeterReadStart(previousMeterData.expectedMeterStart);
      setExpectedMeterStart(previousMeterData.expectedMeterStart);
    }
  }, [previousMeterData, isEdit, meterReadStart]);

  // Check for meter reading discrepancy
  useEffect(() => {
    if (expectedMeterStart && meterReadStart && meterReadStart !== expectedMeterStart) {
      const expected = parseFloat(expectedMeterStart);
      const actual = parseFloat(meterReadStart);
      if (!isNaN(expected) && !isNaN(actual)) {
        const discrepancy = actual - expected;
        if (Math.abs(discrepancy) > 0.01) {
          setShowMeterWarning(true);
          setMeterDiscrepancy(discrepancy);
        } else {
          setShowMeterWarning(false);
          setMeterDiscrepancy(0);
        }
      }
    } else {
      setShowMeterWarning(false);
      setMeterDiscrepancy(0);
    }
  }, [meterReadStart, expectedMeterStart]);

  // Handle QR code deep-link: auto-select well and scroll to field
  useEffect(() => {
    if (qrWellId && !isEdit) {
      const wellId = parseInt(qrWellId, 10);
      if (!isNaN(wellId)) {
        setSelectedWellId(wellId);

        // Auto-scroll to field after a short delay to ensure DOM is ready
        if (qrFocusField) {
          setTimeout(() => {
            const fieldElement = document.getElementById(qrFocusField);
            if (fieldElement) {
              fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              fieldElement.focus();
              // Add a brief highlight effect
              fieldElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
              setTimeout(() => {
                fieldElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
              }, 2000);
            }
          }, 500);
        }
      }
    }
  }, [qrWellId, qrFocusField, isEdit]);

  // Load existing log data
  useEffect(() => {
    if (existingLog) {
      setSelectedWellId(existingLog.wellId);
      setLogDate(new Date(existingLog.logDate).toISOString().split('T')[0]);
      setLogTime(existingLog.logTime || "");
      setOperatorInitial(existingLog.operatorInitial || "");
      setMeterReadStart(existingLog.meterReadStart || "");
      setMeterReadEnd(existingLog.meterReadEnd || "");
      setGpm(existingLog.gpm || "");
      setHours(existingLog.hours || "");
      setRemarks(existingLog.remarks || "");

      // Load custom fields
      if (existingLog.extraJson) {
        setDailyLogExtraJson(existingLog.extraJson as Record<string, any>);
      }

      if (existingLog.chemical) {
        setMeasuredInchesCl2(existingLog.chemical.measuredInchesCl2?.toString() || "");
        setInchesUsedCl2(existingLog.chemical.inchesUsedCl2?.toString() || "");
        setInchesNaOClWithDelivery(existingLog.chemical.inchesNaOClWithDelivery?.toString() || "");
        setLbsCl2(existingLog.chemical.lbsCl2?.toString() || "");
        setFreeResidualPpm(existingLog.chemical.freeResidualPpm?.toString() || "");
        setPsi(existingLog.chemical.psi?.toString() || "");
        setStaticValue(existingLog.chemical.staticValue?.toString() || "");
        setPumpingValue(existingLog.chemical.pumpingValue?.toString() || "");
        setTopOffCl2(existingLog.chemical.topOffCl2?.toString() || "");
        setResidual(existingLog.chemical.residual?.toString() || "");
        setChemicalNotes(existingLog.chemical.chemicalNotes || "");

        // Load chemical custom fields
        if (existingLog.chemical.extraJson) {
          setChemicalLogExtraJson(existingLog.chemical.extraJson as Record<string, any>);
        }
      }
    }
  }, [existingLog]);

  // Load outbox counts on mount
  useEffect(() => {
    const loadOutboxStatus = async () => {
      try {
        const counts = await getOutboxCounts();
        setOutboxCounts(counts);
      } catch (error) {
        console.error('Failed to load outbox counts:', error);
      }
    };

    loadOutboxStatus();
  }, []);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Try to sync when coming online and have pending logs
    const handleOnlineSync = async () => {
      if (navigator.onLine && outboxCounts.pending > 0) {
        try {
          await syncPendingDailyLogs();
          // Refresh counts after sync
          const counts = await getOutboxCounts();
          setOutboxCounts(counts);
          toast.success(`Synced ${outboxCounts.pending} offline logs`);
        } catch (error) {
          toast.error("Failed to sync offline logs");
        }
      }
    };

    const onlineHandler = () => {
      setIsOnline(true);
      handleOnlineSync();
    };

    window.addEventListener('online', onlineHandler);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', onlineHandler);
    };
  }, [outboxCounts.pending]);

  // Get selected well for calculations
  const selectedWell = wells?.find(w => w.id === selectedWellId);
  
  // Preview-only calculation (server will compute final value with multiplier)
  const gallonsPumpedPreview = meterReadStart && meterReadEnd && selectedWell
    ? ((parseFloat(meterReadEnd) - parseFloat(meterReadStart)) * parseFloat(selectedWell.meterMultiplier || "1")).toFixed(2)
    : "";

  // SERVER-DRIVEN chlorine calculations (config-based, temporal)
  const { data: chlorineMetrics } = trpc.chlorineConfig.calculateMetrics.useQuery(
    {
      wellId: selectedWellId!,
      forDate: new Date(logDate),
      inchesUsedCl2: inchesUsedCl2 ? parseFloat(inchesUsedCl2) : null,
    },
    {
      enabled: !!selectedWellId && !!logDate && inchesUsedCl2 !== "",
    }
  );

  // Derived values from server calculation
  const gallonsDelivered = chlorineMetrics?.gallonsDelivered?.toFixed(2) || "";
  const lbsCl2Derived = chlorineMetrics?.lbsCl2?.toFixed(2) || "";
  const hasConfigMissing = inchesUsedCl2 !== "" && chlorineMetrics?.configSource === 'none';

  const filteredWells = wells?.filter(w => 
    w.wellName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isFavorite = favorites?.some(f => f.id === selectedWellId);

  const toggleFavorite = async () => {
    if (!selectedWellId) return;
    
    try {
      if (isFavorite) {
        await removeFavoriteMutation.mutateAsync({ wellId: selectedWellId });
        toast.success("Removed from favorites");
      } else {
        await addFavoriteMutation.mutateAsync({ wellId: selectedWellId });
        toast.success("Added to favorites");
      }
      utils.favorites.list.invalidate();
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  // Helper function to check if error is network-related (for offline fallback)
  // Only enqueue offline sync on real network errors. Do NOT enqueue on HTTP 4xx/5xx TRPCClientError. Show user-visible failure.
  const isNetworkError = (error: any): boolean => {
    if (error instanceof TRPCClientError) {
      return false; // TRPC app errors (validation, DB, etc.) - show to user, don't offline
    }
    return (
      !navigator.onLine ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('ERR_NETWORK') ||
      error?.message?.includes('fetch aborted') ||
      error?.code === 'ERR_NETWORK' ||
      typeof error.status === 'undefined' || // Network errors often have undefined status
      error.status === 0 // Server unreachable
    );
  };

  const handleSubmit = async (status: "draft" | "submitted") => {
    const toOptionalString = (v: unknown) => {
      if (v === null || v === undefined) return undefined;
      if (typeof v === "string") { 
        const s = v.trim(); 
        return s === "" ? undefined : s; 
      }
      if (typeof v === "number") return Number.isFinite(v) ? String(v) : undefined;
      return undefined;
    };

    const toOptionalNumber = (v: unknown) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    const sanitizeDailyLog = (raw: any) => {
      const out: Record<string, any> = { 
        wellId: raw.wellId,
        logDate: raw.logDate,
        status: raw.status
      };

      const assignStr = (k: string, v: unknown) => {
        const s = toOptionalString(v);
        if (s !== undefined) out[k] = s;
      };

      assignStr("logTime", raw.logTime);
      assignStr("operatorInitial", raw.operatorInitial);
      assignStr("remarks", raw.remarks);
      assignStr("editReason", raw.editReason);

      const assignNum = (k: string, v: unknown) => {
        const n = toOptionalNumber(v);
        if (n !== undefined) out[k] = n;
      };

      assignNum("meterReadStart", raw.meterReadStart);
      assignNum("meterReadEnd", raw.meterReadEnd);
      assignNum("gpm", raw.gpm);
      assignNum("hours", raw.hours);

      if (raw.extraJson && Object.keys(raw.extraJson).length > 0) {
        out.extraJson = raw.extraJson;
      }

      Object.keys(out).forEach((key) => {
        if (out[key] === undefined) delete out[key];
      });

      return out;
    };

    const sanitizeChem = (raw: any) => {
      const out: Record<string, any> = { dailyLogId: raw.dailyLogId };

      const assign = (k: string, v: unknown) => {
        const s = toOptionalString(v);
        if (s !== undefined) out[k] = s;
      };

      assign("measuredInchesCl2", raw.measuredInchesCl2);
      assign("inchesUsedCl2", raw.inchesUsedCl2);
      assign("inchesNaOClWithDelivery", raw.inchesNaOClWithDelivery);
      assign("lbsCl2", raw.lbsCl2);
      assign("freeResidualPpm", raw.freeResidualPpm);
      assign("psi", raw.psi);
      assign("staticValue", raw.staticValue);
      assign("pumpingValue", raw.pumpingValue);
      assign("topOffCl2", raw.topOffCl2);
      assign("residual", raw.residual);
      assign("chemicalNotes", raw.chemicalNotes);

      if (raw.extraJson && Object.keys(raw.extraJson).length > 0) {
        out.extraJson = raw.extraJson;
      }

      // Safety: delete undefined
      Object.keys(out).forEach((key) => {
        if (out[key] === undefined) delete out[key];
      });

      return out;
    };

    if (!selectedWellId) {
      toast.error("Please select a well");
      return;
    }

  // Duplicate check is now handled server-side with upsert behavior
  // Client-side needs to provide edit reason for potential upserts

    // Validate required custom fields only when submitting (not draft)
    if (status === "submitted") {
      setShowValidation(true);

      // Check photo requirements - NOTE: Photos not handled in Pilot v1 offline
      const selectedWell = wells?.find(w => w.id === selectedWellId);
      const missingPhotos: string[] = [];

      if (selectedWell) {
        // Check meter photo requirement
        if (selectedWell.requireMeterPhoto) {
          const hasMeterPhoto = fieldPhotos.some(p => p.fieldContext === 'meter_reading') ||
                               photos.some(p => p.attachmentType === 'meter');
          if (!hasMeterPhoto) {
            missingPhotos.push('Meter Reading Photo');
          }
        }

        // Check chlorine photo requirement
        if (selectedWell.requireChlorinePhoto) {
          const hasChlorinePhoto = fieldPhotos.some(p => p.fieldContext === 'chlorine_gauge') ||
                                  photos.some(p => p.attachmentType === 'chlorine_tank');
          if (!hasChlorinePhoto) {
            missingPhotos.push('Chlorine Gauge Photo');
          }
        }

        // Check residual photo requirement
        if (selectedWell.requireResidualPhoto) {
          const hasResidualPhoto = fieldPhotos.some(p => p.fieldContext === 'residual');
          if (!hasResidualPhoto) {
            missingPhotos.push('Residual Measurement Photo');
          }
        }
      }

      if (missingPhotos.length > 0) {
        toast.error(`Missing required photos: ${missingPhotos.join(', ')}. Please attach photos before submitting.`);
        return;
      }

      const missingDailyLogFields: string[] = [];
      const missingChemicalLogFields: string[] = [];

      // Check daily log required fields
      dailyLogCustomFields?.forEach((field: any) => {
        if (field.isRequired) {
          const value = dailyLogExtraJson[field.fieldKey];
          if (value === undefined || value === null || value === "") {
            missingDailyLogFields.push(field.label);
          }
        }
      });

      // Check chemical log required fields
      chemicalLogCustomFields?.forEach((field: any) => {
        if (field.isRequired) {
          const value = chemicalLogExtraJson[field.fieldKey];
          if (value === undefined || value === null || value === "") {
            missingChemicalLogFields.push(field.label);
          }
        }
      });

      const allMissingFields = [...missingDailyLogFields, ...missingChemicalLogFields];

      if (allMissingFields.length > 0) {
        toast.error(`Missing required fields: ${allMissingFields.join(", ")}`);
        return;
      }
    } else {
      setShowValidation(false); // Don't show validation for drafts
    }

    let dailyLogSaved = false;
    let chemicalErrorMsg = '';
    let photoErrorMsgs: string[] = [];
    let photoUploadsFailed = 0;

    try {
      const logDataRaw = {
        wellId: selectedWellId,
        logDate: new Date(logDate),
        logTime,
        operatorInitial,
        meterReadStart,
        meterReadEnd,
        gpm,
        hours,
        remarks,
        editReason,
        status,
        extraJson: dailyLogExtraJson,
      };


      const logData = sanitizeDailyLog(logDataRaw);
      console.log("daily log payload:", logData);

      let dailyLogId: number;

      // Try to save daily log to server first
      try {
        if (isEdit) {
          await updateLogMutation.mutateAsync({ id: logId, ...logData });
          dailyLogId = logId;
        } else {
        const createResult = await createLogMutation.mutateAsync(logData);
        dailyLogId = createResult.id;

        dailyLogSaved = true;
        }

        dailyLogSaved = true;
      } catch (networkErr: any) {
        // Check if this is a network error that should trigger offline fallback
        if (isNetworkError(networkErr)) {
          console.log('[Offline] Network error detected, enqueueing for offline sync:', networkErr.message);

          // Enqueue for offline sync - Pilot v1 offline behavior
          const offlineEntryRaw = {
            wellId: selectedWellId,
            logDate,
            logTime,
            operatorInitial,
            meterReadStart,
            meterReadEnd,
            gpm,
            hours,
            remarks,
            status,
            extraJson: dailyLogExtraJson,
            editReason,
          };

          const offlineEntry = sanitizeDailyLog(offlineEntryRaw);
          console.log("offline daily log payload:", offlineEntry);

          try {
            await enqueueDailyLog(offlineEntry);
            toast.success("No signal — saved locally. Will sync later.");
            navigate("/logs"); // User can continue working
            return; // Exit since we saved locally
          } catch (enqueueError) {
            console.error('[Offline] Failed to enqueue log:', enqueueError);
            toast.error("Failed to save locally - please try again when connected");
            return;
          }
        } else {
          // Server-side validation error or other issue - re-throw, don't enqueue
          throw networkErr;
        }
      }

      // Continue with chemical data and photos only if we successfully saved to server
      // Save chemical data (non-critical - catch errors)
      if (dailyLogId) {
        try {
          const chemPayload = buildChemicalUpsertPayload(dailyLogId, {
            measuredInchesCl2,
            inchesUsedCl2,
            inchesNaOClWithDelivery,
            lbsCl2,
            freeResidualPpm,
            psi,
            staticValue,
            pumpingValue,
            topOffCl2,
            residual,
            chemicalNotes,
            extraJson: chemicalLogExtraJson
          });
          console.log("chemical upsert payload:", chemPayload);

          if (Object.keys(chemPayload).length === 1) {
            console.log("No chemical fields entered; skipping chemical upsert");
          } else {
            // Runtime assert: all non-dailyLogId chemical fields must be non-empty strings (skip extraJson)
            for (const [k, v] of Object.entries(chemPayload)) {
              if (k !== "dailyLogId" && k !== "extraJson") {
                if (v == null || typeof v !== "string" || v === "") {
                  throw new Error(`Invalid chemical payload: ${k} = ${JSON.stringify(v)} (must be non-empty string)`);
                }
              }
            }
            await upsertChemicalMutation.mutateAsync(chemPayload);
          }
          } catch (chemErr) {
            console.error('Failed to save chemical data:', chemErr);
          chemicalErrorMsg = (chemErr as Error).message || 'Unknown error';
        }


        // Upload photos (non-critical - catch errors per photo)
        for (const photo of photos) {
          try {
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve) => {
              reader.onloadend = () => {
                const result = reader.result as string;
                // Remove data:image/...;base64, prefix
                const base64Data = result.split(',')[1] || '';
                resolve(base64Data);
              };
              reader.readAsDataURL(photo.file);
            });

            const selectedWell = wells?.find(w => w.id === selectedWellId);
            let uploadFile = photo.file;
            let fileName = photo.file.name;
            if (!fileName) {
              fileName = `photo-${Date.now()}.jpg`;
              uploadFile = new File([photo.file], fileName, { type: photo.file.type || "image/jpeg" });
            }

            console.log('photo upload input:', { fileName, mimeType: uploadFile.type, fileSize: uploadFile.size, attachmentType: photo.attachmentType });

            await uploadPhotoMutation.mutateAsync({
              dailyLogId,
              fileName,
              fileData: base64,
              mimeType: uploadFile.type,
              fileSize: uploadFile.size,
              attachmentType: photo.attachmentType,
              // Enhanced metadata
              wellId: selectedWellId || undefined,
              wellName: selectedWell?.wellName,
              logDate,
              operatorInitial,
            });

      } catch (err: any) {
        console.error('Failed to upload photo:', err);
        photoErrorMsgs.push(err.message || 'Unknown upload error');
        photoUploadsFailed++;
      }
        }

        // Upload field-specific photos
        for (const photo of fieldPhotos) {
          try {
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve) => {
              reader.onloadend = () => {
                const result = reader.result as string;
                const base64Data = result.split(',')[1] || '';
                resolve(base64Data);
              };
              reader.readAsDataURL(photo.file);
            });

            const selectedWell = wells?.find(w => w.id === selectedWellId);
            const fileName = photo.file.name || `dailylog-${dailyLogId}-${Date.now()}.jpg`;
            const uploadFile = photo.file.name ? photo.file : new File([photo.file], fileName, { type: photo.file.type || "image/jpeg" });

            console.log('field photo upload input:', { fileName, mimeType: uploadFile.type, fileSize: uploadFile.size, fieldContext: photo.fieldContext });

            await uploadPhotoMutation.mutateAsync({
              dailyLogId,
              fileName,
              fileData: base64,
              mimeType: uploadFile.type,
              fileSize: uploadFile.size,
              attachmentType: 'other',
              fieldContext: photo.fieldContext,
              // Enhanced metadata
              wellId: selectedWellId || undefined,
              wellName: selectedWell?.wellName,
              logDate,
              operatorInitial,
            });


          } catch (err: any) {
            console.error('Failed to upload field photo:', err);
            photoErrorMsgs.push(err.message || 'Unknown upload error');
            photoUploadsFailed++;
          }
        }
      }

      // Show appropriate success message
      setShowValidation(false);

      const hasChemicalError = chemicalErrorMsg !== '';
      const hasPhotoErrors = photoErrorMsgs.length > 0;

      if (hasChemicalError && hasPhotoErrors) {
        toast.error(`Log ${isEdit ? 'updated' : 'saved'} with errors. Chemicals failed: ${chemicalErrorMsg}. Photos failed: ${photoErrorMsgs.join(', ')}`);
      } else if (hasChemicalError) {
        toast.error(`Log ${isEdit ? 'updated' : 'saved'}, but chemicals failed: ${chemicalErrorMsg}`);
      } else if (hasPhotoErrors) {
        toast.error(`Log ${isEdit ? 'updated' : 'saved'}, but photos failed: ${photoErrorMsgs.join(', ')}`);
      } else {
        toast.success(`Log ${isEdit ? 'updated' : 'saved'} successfully`);
      }

      navigate("/logs");
    } catch (error: any) {
      // Only show error if daily log save failed (and it wasn't already handled by offline fallback)
      if (!dailyLogSaved) {
        toast.error(error.message || "Failed to save log");
      }
      setShowValidation(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">
              {isEdit ? "Edit Daily Log" : "New Daily Log"}
            </h1>
          </div>
        </div>
      </header>

      {/* Offline Banner - show when we have pending offline logs */}
      {(outboxCounts.pending > 0 || outboxCounts.failed > 0) && (
        <div className="border-b bg-orange-50 dark:bg-orange-950/20">
          <div className="container py-3">
            <div className="flex items-center gap-3">
              <WifiOff className="h-4 w-4 text-orange-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  {outboxCounts.pending > 0 && (
                    <span>{outboxCounts.pending} log{outboxCounts.pending !== 1 ? 's' : ''} saved locally and pending sync</span>
                  )}
                  {outboxCounts.pending > 0 && outboxCounts.failed > 0 && <span> • </span>}
                  {outboxCounts.failed > 0 && (
                    <span>{outboxCounts.failed} log{outboxCounts.failed !== 1 ? 's' : ''} failed to save</span>
                  )}
                </p>
                <p className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                  {!isOnline ? 'Waiting for network connection...' : outboxCounts.pending > 0 ? 'Will sync automatically when conditions allow.' : 'Check System Health for details.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="container py-6 max-w-4xl">
        <Tabs defaultValue="basic" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="chemical">Chlorine & Chemical</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            {/* Well Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Well</CardTitle>
                <CardDescription>Choose the well for this log entry</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="search">Search Wells</Label>
                  <Input
                    id="search"
                    placeholder="Search by name or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Favorites */}
                {favorites && favorites.length > 0 && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Favorites</Label>
                    <div className="flex flex-wrap gap-2">
                      {favorites.map((well) => (
                        <Badge
                          key={well.id}
                          variant={selectedWellId === well.id ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => setSelectedWellId(well.id)}
                        >
                          <Star className="w-3 h-3 mr-1 fill-current" />
                          {well.wellName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Wells */}
                {recentWells && recentWells.length > 0 && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Recent</Label>
                    <div className="flex flex-wrap gap-2">
                      {recentWells.map((well) => (
                        <Badge
                          key={well.id}
                          variant={selectedWellId === well.id ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => setSelectedWellId(well.id)}
                        >
                          {well.wellName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Wells */}
                <div>
                  <Label htmlFor="well">All Wells</Label>
                  <Select
                    value={selectedWellId?.toString() || ""}
                    onValueChange={(value) => setSelectedWellId(parseInt(value))}
                  >
                    <SelectTrigger id="well">
                      <SelectValue placeholder="Select a well" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredWells?.map((well) => (
                        <SelectItem key={well.id} value={well.id.toString()}>
                          {well.wellName} {well.location && `- ${well.location}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedWellId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleFavorite}
                  >
                    <Star className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-current' : ''}`} />
                    {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Basic Log Info */}
            <Card>
              <CardHeader>
                <CardTitle>Log Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="date" 
                      label="Date" 
                      required
                      helpText={DAILY_LOG_HELP_TEXT.date}
                    />
                    <Input
                      id="date"
                      type="date"
                      value={logDate}
                      onChange={(e) => setLogDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel 
                      htmlFor="time" 
                      label="Time"
                      helpText={DAILY_LOG_HELP_TEXT.time}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        id="time"
                        type="time"
                        value={logTime}
                        onChange={(e) => setLogTime(e.target.value)}
                        className="flex-1"
                      />
                      {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCorrectionField({
                              fieldName: 'logTime',
                              fieldLabel: 'Log Time',
                              currentValue: logTime
                            });
                            setCorrectionModalOpen(true);
                          }}
                          title="Correct this field"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <FieldLabel 
                    htmlFor="operator" 
                    label="Operator Initial"
                    helpText={DAILY_LOG_HELP_TEXT.operatorInitial}
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      id="operator"
                      maxLength={10}
                      value={operatorInitial}
                      onChange={(e) => setOperatorInitial(e.target.value)}
                      className="flex-1"
                    />
                    {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCorrectionField({
                            fieldName: 'operatorInitial',
                            fieldLabel: 'Operator Initial',
                            currentValue: operatorInitial
                          });
                          setCorrectionModalOpen(true);
                        }}
                        title="Correct this field"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="start" 
                      label="Meter Read Start"
                      helpText={DAILY_LOG_HELP_TEXT.meterReadStart}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        id="start"
                        type="number"
                        step="0.01"
                        value={meterReadStart}
                        onChange={(e) => setMeterReadStart(e.target.value)}
                        className={`flex-1 ${showMeterWarning ? "border-yellow-500" : ""}`}
                      />
                      {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCorrectionField({
                              fieldName: 'meterReadStart',
                              fieldLabel: 'Meter Read Start',
                              currentValue: meterReadStart
                            });
                            setCorrectionModalOpen(true);
                          }}
                          title="Correct this field"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {expectedMeterStart && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Expected: {parseFloat(expectedMeterStart).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <FieldLabel 
                      htmlFor="end" 
                      label="Meter Read End"
                      helpText={DAILY_LOG_HELP_TEXT.meterReadEnd}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        id="end"
                        type="number"
                        step="0.01"
                        value={meterReadEnd}
                        onChange={(e) => setMeterReadEnd(e.target.value)}
                        className="flex-1"
                      />
                      <FieldPhotoCapture
                        fieldContext="meterReadEnd"
                        fieldLabel="Meter Reading"
                        onPhotoCapture={handleFieldPhotoCapture}
                        onPhotoRemove={handleFieldPhotoRemove}
                        photos={fieldPhotos}
                      />
                      {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCorrectionField({
                              fieldName: 'meterReadEnd',
                              fieldLabel: 'Meter Read End',
                              currentValue: meterReadEnd
                            });
                            setCorrectionModalOpen(true);
                          }}
                          title="Correct this field"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {showMeterWarning && (
                  <Alert className="border-yellow-500/50 bg-yellow-500/10">
                    <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                      <strong>Meter Reading Discrepancy:</strong> Start reading ({parseFloat(meterReadStart).toLocaleString()}) differs from previous end reading ({parseFloat(expectedMeterStart!).toLocaleString()}) by {meterDiscrepancy > 0 ? '+' : ''}{meterDiscrepancy.toLocaleString()}. This discrepancy will be logged for audit review. You can still save this log.
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <FieldLabel 
                    htmlFor="gallonsPumped" 
                    label="Gallons Pumped"
                    helpText={DAILY_LOG_HELP_TEXT.gallonsPumped}
                  />
                  <Input value={gallonsPumpedPreview} disabled className="bg-muted" />
                  {selectedWell?.meterMultiplier && selectedWell.meterMultiplier !== "1" && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Multiplier: {selectedWell.meterMultiplier}x
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="gpm" 
                      label="GPM"
                      helpText={DAILY_LOG_HELP_TEXT.gpm}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        id="gpm"
                        type="number"
                        step="0.01"
                        value={gpm}
                        onChange={(e) => setGpm(e.target.value)}
                        className="flex-1"
                      />
                      {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCorrectionField({
                              fieldName: 'gpm',
                              fieldLabel: 'GPM',
                              currentValue: gpm
                            });
                            setCorrectionModalOpen(true);
                          }}
                          title="Correct this field"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {selectedWell?.hasHourMeter && (
                    <div>
                      <FieldLabel 
                        htmlFor="hours" 
                        label="Hours"
                        helpText={DAILY_LOG_HELP_TEXT.hours}
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          id="hours"
                          type="number"
                          step="0.01"
                          value={hours}
                          onChange={(e) => setHours(e.target.value)}
                          className="flex-1"
                        />
                        {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCorrectionField({
                                fieldName: 'hours',
                                fieldLabel: 'Hours',
                                currentValue: hours
                              });
                              setCorrectionModalOpen(true);
                            }}
                            title="Correct this field"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <FieldLabel 
                    htmlFor="remarks" 
                    label="Remarks"
                    helpText={DAILY_LOG_HELP_TEXT.remarks}
                  />
                  <div className="flex items-start gap-2">
                    <Textarea
                      id="remarks"
                      rows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="flex-1"
                    />
                    {isEdit && !(existingLog?.status === 'locked' && user?.role !== 'admin') && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCorrectionField({
                            fieldName: 'remarks',
                            fieldLabel: 'Remarks',
                            currentValue: remarks
                          });
                          setCorrectionModalOpen(true);
                        }}
                        title="Correct this field"
                        className="mt-1"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Edit Reason Field - shown when creating new log (may result in upsert) */}
                {!isEdit && (
                  <div>
                    <FieldLabel
                      htmlFor="editReason"
                      label="Edit Reason (if updating existing log)"
                      required={false}
                      helpText="Required if you are updating an existing log for this well/date. Leave blank for new logs."
                    />
                    <Textarea
                      id="editReason"
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      rows={2}
                      placeholder="e.g., Corrected meter reading, updated operator information, added missing notes (leave blank for new logs)"
                    />
                  </div>
                )}

                {/* Custom Fields for Daily Log */}
                {dailyLogCustomFields && dailyLogCustomFields.length > 0 && (
                  <DynamicCustomFields
                    fields={dailyLogCustomFields as any}
                    values={dailyLogExtraJson}
                    onChange={(fieldKey, value) => {
                      setDailyLogExtraJson(prev => ({ ...prev, [fieldKey]: value }));
                    }}
                    showValidation={showValidation}
                  />
                )}
              </CardContent>
            </Card>

            {/* Corrections History - shown only in edit mode */}
            {isEdit && existingLog?.corrections && existingLog.corrections.length > 0 && (
              <CorrectionsHistory corrections={existingLog.corrections} />
            )}
          </TabsContent>

          <TabsContent value="chemical" className="space-y-6">
            {selectedWell?.hasChlorineGauge && (
              <Card>
                <CardHeader>
                  <CardTitle>Chlorine Tracking</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="measured" 
                      label="Measured Inches Cl₂"
                      helpText={CHEMICAL_LOG_HELP_TEXT.measuredInchesCl2}
                    />
                    <div className="flex items-center gap-2">
                      <Input
                        id="measured"
                        type="number"
                        step="0.01"
                        value={measuredInchesCl2}
                        onChange={(e) => setMeasuredInchesCl2(e.target.value)}
                        className="flex-1"
                      />
                      <FieldPhotoCapture
                        fieldContext="measuredInchesCl2"
                        fieldLabel="Chlorine Gauge"
                        onPhotoCapture={handleFieldPhotoCapture}
                        onPhotoRemove={handleFieldPhotoRemove}
                        photos={fieldPhotos}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="used">Inches Used Cl₂</Label>
                    <Input
                      id="used"
                      type="number"
                      step="0.01"
                      value={inchesUsedCl2}
                      onChange={(e) => setInchesUsedCl2(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="delivery">Inches NaOCl with Delivery</Label>
                    <Input
                      id="delivery"
                      type="number"
                      step="0.01"
                      value={inchesNaOClWithDelivery}
                      onChange={(e) => setInchesNaOClWithDelivery(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Gallons Delivered (Auto-calculated)</Label>
                    <Input value={gallonsDelivered} disabled />
                  </div>
                </div>

                {/* Missing config warning */}
                {hasConfigMissing && (
                  <Alert className="border-orange-500/50 bg-orange-500/10">
                    <AlertDescription className="text-orange-800 dark:text-orange-200">
                      <strong>Chlorine Config Missing:</strong> No configuration found for this well/date. Cannot calculate gallons delivered or lbs Cl₂. Contact administrator to configure chlorine system settings.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="lbs" 
                      label="lbs of Cl₂ (Auto-calculated)"
                      helpText={CHEMICAL_LOG_HELP_TEXT.lbsCl2}
                    />
                    <Input
                      id="lbs"
                      value={lbsCl2Derived}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Calculated from config
                    </p>
                  </div>
                  <div>
                    <FieldLabel 
                      htmlFor="residual-ppm" 
                      label="Free Cl₂ Residual (ppm)"
                      helpText={CHEMICAL_LOG_HELP_TEXT.freeResidualPpm}
                    />
                    <Input
                      id="residual-ppm"
                      type="number"
                      step="0.01"
                      value={freeResidualPpm}
                      onChange={(e) => setFreeResidualPpm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="psi" 
                      label="PSI"
                      helpText={CHEMICAL_LOG_HELP_TEXT.psi}
                    />
                    <Input
                      id="psi"
                      type="number"
                      step="0.01"
                      value={psi}
                      onChange={(e) => setPsi(e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel 
                      htmlFor="static" 
                      label="Static"
                      helpText={CHEMICAL_LOG_HELP_TEXT.staticValue}
                    />
                    <Input
                      id="static"
                      type="number"
                      step="0.01"
                      value={staticValue}
                      onChange={(e) => setStaticValue(e.target.value)}
                    />
                  </div>
                  <div>
                    <FieldLabel 
                      htmlFor="pumping" 
                      label="Pumping"
                      helpText={CHEMICAL_LOG_HELP_TEXT.pumpingValue}
                    />
                    <Input
                      id="pumping"
                      type="number"
                      step="0.01"
                      value={pumpingValue}
                      onChange={(e) => setPumpingValue(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel 
                      htmlFor="topoff" 
                      label="Top Off Cl₂"
                      helpText={CHEMICAL_LOG_HELP_TEXT.topOffCl2}
                    />
                    <Input
                      id="topoff"
                      type="number"
                      step="0.01"
                      value={topOffCl2}
                      onChange={(e) => setTopOffCl2(e.target.value)}
                    />
                  </div>
                  {selectedWell?.hasTowerResidual && (
                    <div>
                      <FieldLabel 
                        htmlFor="residual" 
                        label="Residual"
                        helpText={CHEMICAL_LOG_HELP_TEXT.residual}
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          id="residual"
                          type="number"
                          step="0.01"
                          value={residual}
                          onChange={(e) => setResidual(e.target.value)}
                          className="flex-1"
                        />
                        <FieldPhotoCapture
                          fieldContext="residual"
                          fieldLabel="Tower Residual"
                          onPhotoCapture={handleFieldPhotoCapture}
                          onPhotoRemove={handleFieldPhotoRemove}
                          photos={fieldPhotos}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="chem-notes">Chemical Notes</Label>
                  <Textarea
                    id="chem-notes"
                    rows={3}
                    value={chemicalNotes}
                    onChange={(e) => setChemicalNotes(e.target.value)}
                  />
                </div>
                
                {/* Custom Fields for Chemical Log */}
                {chemicalLogCustomFields && chemicalLogCustomFields.length > 0 && (
                  <DynamicCustomFields
                    fields={chemicalLogCustomFields as any}
                    values={chemicalLogExtraJson}
                    onChange={(fieldKey, value) => {
                      setChemicalLogExtraJson(prev => ({ ...prev, [fieldKey]: value }));
                    }}
                    showValidation={showValidation}
                  />
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>
          
          <TabsContent value="photos" className="space-y-6">
            {isEdit && logId && (
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  Existing Photos
                </h3>
                <PhotoGallery dailyLogId={logId} canDelete />
              </div>
            )}
            <Card>
              <CardHeader>
                <CardTitle>Add New Photos</CardTitle>
                <CardDescription>
                  Attach photos of meter readings, chlorine tank gauges, or other documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PhotoUpload photos={photos} onPhotosChange={setPhotos} isOffline={!isOnline} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button
            onClick={() => handleSubmit("draft")}
            variant="outline"
            className="flex-1"
            disabled={createLogMutation.isPending || updateLogMutation.isPending}
          >
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSubmit("submitted")}
            className="flex-1"
            disabled={createLogMutation.isPending || updateLogMutation.isPending}
          >
            Submit Log
          </Button>
        </div>
      </main>
      
      {/* Duplicate Log Modal */}
      <DuplicateLogModal
        open={duplicateModalOpen}
        onOpenChange={setDuplicateModalOpen}
        existingLog={existingDuplicateLog}
        onOpenExisting={() => {
          if (existingDuplicateLog) {
            navigate(`/logs/${existingDuplicateLog.id}/edit`);
          }
        }}
        onCancel={() => {
          setDuplicateModalOpen(false);
          setExistingDuplicateLog(null);
        }}
      />

      {/* Correction Field Modal */}
      {correctionField && logId && (
        <CorrectFieldModal
          open={correctionModalOpen}
          onOpenChange={setCorrectionModalOpen}
          dailyLogId={logId}
          fieldName={correctionField.fieldName}
          fieldLabel={correctionField.fieldLabel}
          currentValue={correctionField.currentValue}
          onSuccess={() => {
            utils.dailyLogs.getById.invalidate({ id: logId });
            setCorrectionModalOpen(false);
            setCorrectionField(null);
          }}
        />
      )}
    </div>
  );
}
